import { ListObjectsCommand, PutObjectCommand } from "@aws-sdk/client-s3";
import {
  S3_PHOTOS_BUCKET,
  albumPrefixes,
  photoUrl,
  thumbKey,
} from "./media";
import { applyPhotoOrder } from "./photoOrder";
import {
  districtLabel,
  formatPrice,
  listingTitle,
  plural,
} from "./public/utils/format";

/**
 * Writes the social-media preview page for one listing, straight from the
 * realtor panel, the moment the listing is saved.
 *
 * Why here. The site is a SPA, so crawlers see an empty index.html; a tiny
 * pre-rendered `og/<id>.html` per listing is what gives Telegram and Viber the
 * apartment's own photo (SHARE_PREVIEW.md explains the whole mechanism). Those
 * pages were only ever produced in bulk by scripts/generate_og_pages.py, which
 * means a listing added today had no preview until someone remembered to run
 * it — and the failure is silent: the link works, it just shows the logo.
 *
 * Doing it here makes a new listing shareable the second it is saved, with no
 * Lambda, no schedule and nothing to remember. The bulk script stays for
 * backfills and for repairing the folder after a deploy wipes it.
 *
 * The two must agree, so this file mirrors the Python one: same template, same
 * `?full=1` escape hatch, same title and description wording. The pricing,
 * plural and district rules are *imported* from src/public/utils/format.js
 * rather than re-implemented — the Python script is the only real mirror, and
 * one is enough.
 *
 * IAM: the realtor roles need `s3:PutObject` on `leocity.estate/og/*`.
 * Failure here never blocks a save — see the call site in AppRealtor.js.
 */

/** The bucket the website itself is served from — not the photo bucket. */
export const SITE_BUCKET = "leocity.estate";
export const SITE_URL = "https://leocity.estate";
export const OG_PREFIX = "og/";

/** Matches CACHE_CONTROL in scripts/generate_og_pages.py. */
const OG_CACHE_CONTROL = "public, max-age=600";

/** html.escape(..., quote=True) in Python escapes exactly these five. */
function esc(value) {
  return String(value === null || value === undefined ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;");
}

/** Mirrors listing_description() in scripts/generate_og_pages.py. */
function listingDescription(item) {
  var bits = [];
  var district = districtLabel(item.region);
  if (district) bits.push(district);
  if (item.floor) bits.push(parseInt(item.floor, 10) + " поверх");
  if (item.rooms) {
    var n = parseInt(item.rooms, 10);
    bits.push(n + " " + plural(n, "кімната", "кімнати", "кімнат"));
  }
  if (item.state) bits.push(String(item.state).trim().toLowerCase());

  var head = bits.length ? "Львів, " + bits.join(", ") : "Львів";
  return (
    head +
    ". " +
    formatPrice(item.cost, item.currency) +
    ". Перевірене оголошення LeoCity."
  );
}

export function buildOgHtml(item, imageUrl, size) {
  var title = esc(listingTitle(item) + " — " + formatPrice(item.cost, item.currency));
  var description = esc(listingDescription(item));
  var url = esc(SITE_URL + "/kvartyra/" + item.id);

  // ?full=1 is the escape hatch for STRICT_BROWSER_CHECK in
  // scripts/cloudfront-og-rewrite.js: with it, the function stops rewriting and
  // serves the SPA, so a browser that lands here cannot be bounced in a loop.
  var urlFull = url + "?full=1";

  var imageTags = "";
  var twitterCard = '<meta name="twitter:card" content="summary">';
  var twitterImage = "";

  if (imageUrl) {
    var safe = esc(imageUrl);
    var tags = [
      '<meta property="og:image" content="' + safe + '">',
      '<meta property="og:image:secure_url" content="' + safe + '">',
    ];
    if (size && size.w && size.h) {
      // Viber often drops an image with no declared size.
      tags.push('<meta property="og:image:width" content="' + size.w + '">');
      tags.push('<meta property="og:image:height" content="' + size.h + '">');
    }
    tags.push('<meta property="og:image:alt" content="' + title + '">');
    imageTags = tags.join("\n");
    twitterCard = '<meta name="twitter:card" content="summary_large_image">';
    twitterImage = '<meta name="twitter:image" content="' + safe + '">';
  }

  return (
    '<!DOCTYPE html>\n<html lang="uk">\n<head>\n<meta charset="utf-8">\n' +
    "<title>" + title + "</title>\n" +
    '<meta name="description" content="' + description + '">\n' +
    '<link rel="canonical" href="' + url + '">\n\n' +
    '<meta property="og:type" content="website">\n' +
    '<meta property="og:site_name" content="LeoCity">\n' +
    '<meta property="og:locale" content="uk_UA">\n' +
    '<meta property="og:url" content="' + url + '">\n' +
    '<meta property="og:title" content="' + title + '">\n' +
    '<meta property="og:description" content="' + description + '">\n' +
    imageTags + "\n" +
    twitterCard + "\n" +
    '<meta name="twitter:title" content="' + title + '">\n' +
    '<meta name="twitter:description" content="' + description + '">\n' +
    (twitterImage ? twitterImage + "\n" : "") +
    "</head>\n<body>\n" +
    '<p><a href="' + urlFull + '">' + title + "</a></p>\n" +
    "<script>location.replace(" + JSON.stringify(urlFull) + ");</script>\n" +
    "</body>\n</html>\n"
  );
}

/** Every non-folder key under one prefix, in the order S3 returns them. */
async function listKeys(s3Client, prefix) {
  var data = await s3Client.send(
    new ListObjectsCommand({ Bucket: S3_PHOTOS_BUCKET, Prefix: prefix })
  );
  return (data.Contents || [])
    .map(function (entry) {
      return entry.Key;
    })
    .filter(function (key) {
      return key && !/\/$/.test(key);
    })
    .sort();
}

/**
 * Real pixel size of the preview image.
 *
 * The Python script reads this from the file header with a ranged GET; in the
 * browser the image is already fetchable, and `naturalWidth` needs no CORS.
 */
function measureImage(url) {
  return new Promise(function (resolve) {
    if (typeof Image === "undefined") return resolve(null);

    var img = new Image();
    var settled = false;
    var timer = setTimeout(function () {
      finish(null);
    }, 8000);

    function finish(value) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    }

    img.onload = function () {
      finish(
        img.naturalWidth && img.naturalHeight
          ? { w: img.naturalWidth, h: img.naturalHeight }
          : null
      );
    };
    img.onerror = function () {
      finish(null);
    };
    img.src = url;
  });
}

/**
 * Build and upload og/<id>.html for one listing.
 *
 * Picks the same photo the public site shows: the listing's chosen cover —
 * the first name in `photoOrder`, or the first key alphabetically when no
 * order has been set — preferring its thumbnail. Resolves to true on success;
 * the caller decides what a false means (it should never be fatal).
 */
export async function publishOgPage(s3Client, item) {
  if (!s3Client || !item || !item.id) return false;

  var prefixes = albumPrefixes(item.id);
  var originals = await listKeys(s3Client, prefixes[0]);
  var thumbs = await listKeys(s3Client, prefixes[1]);

  // Same ordering rule as the cards and the catalogue, so all three agree on
  // which photo is the cover.
  var ordered = applyPhotoOrder(
    originals.map(function (key) {
      return { key: key, name: key.split("/").pop() };
    }),
    item.photoOrder
  );
  var first = ordered.length ? ordered[0].key : null;
  var imageKey = null;
  if (first) {
    imageKey = thumbs.indexOf(thumbKey(first)) !== -1 ? thumbKey(first) : first;
  }

  var imageUrl = imageKey ? photoUrl(imageKey) : null;
  var size = imageUrl ? await measureImage(imageUrl) : null;
  var html = buildOgHtml(item, imageUrl, size);

  await s3Client.send(
    new PutObjectCommand({
      Bucket: SITE_BUCKET,
      Key: OG_PREFIX + item.id + ".html",
      // A Blob rather than a string: the page is full of Cyrillic and this
      // pins the encoding to UTF-8 regardless of how the SDK would guess.
      Body: new Blob([html], { type: "text/html; charset=utf-8" }),
      ContentType: "text/html; charset=utf-8",
      CacheControl: OG_CACHE_CONTROL,
    })
  );

  return true;
}
